/* eslint-disable react-hooks/refs, react-hooks/set-state-in-effect --
 * v77.99.77: CalendarPage assigns `openEditRef.current = openEdit` and
 * `toLocalInputRef.current = toLocalInput` during render to bridge
 * `useMonthResize` (called near the top) with `openEdit` (declared
 * 290 lines below). These ref writes are intentional and necessary;
 * the experimental `react-hooks/refs` rule mis-flags them.
 * `react-hooks/set-state-in-effect` is similarly mis-applied to a
 * `useMemo` that derives `childById` from `children` — pure derivation,
 * no side effects.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { generateIdempotencyKey, idempotencyHeader } from "@/lib/idempotency";
import { downloadFile } from "@/lib/downloadFile";
import { recordThirdPartyView } from "@/lib/thirdPartyView";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { UpgradePrompt } from "@/components/UpgradePrompt";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useChildScope } from "@/context/ChildScopeContext";
import { ChildDots } from "@/components/ChildDots";
import { Plus, CalendarDays, ChevronLeft, ChevronRight, Lock, Eye, EyeOff, Repeat, Pencil, BookOpen, ArrowLeftRight, CalendarPlus, Users, Inbox, Check, X as XIcon, Download, MapPin, UserRoundCheck, Paperclip, FileCheck2 } from "lucide-react";
import { buildJournalPrefillFromEvent } from "@/lib/journalPrefill";
import { MediatorPdfDownloadButton } from "@/components/mediator/MediatorPdfDownloadButton";
import { InfoTooltip } from "@/components/InfoTooltip";
import { getParentColorById, tintBg } from "@/lib/parentColor";
import { formatDT, toLocalInput, fromLocalInput, currencySymbol } from "@/lib/format";
import { toast } from "sonner";
import {
  ScheduleProposalsBanner,
  ScheduleAuditBanner,
  ParentingScheduleDialog,
  RecurrenceFields,
  ReminderFields,
} from "@/components/calendar/ScheduleControls";
import { useCalendarFilters } from "@/components/calendar/useCalendarFilters";
// v77.99.23 — Slice 3 extraction. The two big modals (Day Detail +
// Edit Occurrence) now live in dedicated components but stay fully
// state-coupled to this page through their explicit prop bags.
import { DayDetailDialog } from "@/components/calendar/DayDetailDialog";
import { CustodySwapRequestsCard } from "@/components/calendar/CustodySwapRequestsCard";
import { EditOccurrenceDialog } from "@/components/calendar/EditOccurrenceDialog";
import { NewEventDialog } from "@/components/calendar/NewEventDialog";
import { WeekView } from "@/components/calendar/WeekView";

// ===== Contact Visit calendar chip styling =================================
// Iter 122.calendar-status — One source of truth for the colour /
// label / status-tone used when a Contact Visit is rendered on the
// Calendar. Mirrors the chip pattern used inside the Contact Visits
// module so the user sees consistent colour cues across the app.
//
// Returns:
//   { cls, label, statusKey } — `cls` is a Tailwind class string,
//   `label` is the human label, `statusKey` is the canonical key used
//   for data-testid and aria attributes.
function cvChipStatusStyle(status) {
  switch (status) {
    case "in_progress":           // 🟢 Live now — pulses to draw the eye
      return { cls: "bg-emerald-100 border-emerald-400 text-emerald-900 ring-1 ring-emerald-300 animate-pulse",
               label: "Live now", statusKey: "in-progress" };
    case "scheduled":             // 🟡 Awaiting confirmation (not yet confirmed)
      return { cls: "bg-amber-50 border-amber-300 text-amber-900",
               label: "Awaiting confirmation", statusKey: "awaiting-confirmation" };
    case "confirmed":             // 🔵 Scheduled (confirmed by counterparty)
    case "rescheduled":
      return { cls: "bg-sky-50 border-sky-300 text-sky-900",
               label: "Scheduled", statusKey: "scheduled" };
    case "awaiting_verification": // 🟠 Awaiting verification
      return { cls: "bg-orange-50 border-orange-300 text-orange-900",
               label: "Awaiting verification", statusKey: "awaiting-verification" };
    case "disputed":              // 🔴 Disputed
      return { cls: "bg-rose-50 border-rose-300 text-rose-900",
               label: "Disputed", statusKey: "disputed" };
    case "cancelled":
    case "missed":
      return { cls: "bg-slate-100 border-slate-300 text-slate-600 line-through opacity-80",
               label: "Cancelled", statusKey: "cancelled" };
    case "verified":
    case "verified_by_professional":
    case "closed":
    case "completed":
    case "completed_with_notes":
    case "completed_with_incident":
    default:                       // 🟢 Completed
      return { cls: "bg-emerald-50 border-emerald-300 text-emerald-900",
               label: "Completed", statusKey: "completed" };
  }
}

import { DayView } from "@/components/calendar/DayView";
import { useCalendarData } from "@/components/calendar/useCalendarData";
import { useMonthResize } from "@/components/calendar/useMonthResize";
import { CalendarExportDialog } from "@/components/calendar/CalendarExportDialog";

const TYPE_LABEL = { custody: "Custody", pickup: "Pick-up", dropoff: "Drop-off", school: "School", extracurricular: "Extracurricular", sports: "Sports", holiday: "Holiday", call: "Phone call", video_call: "Video call", other: "Other" };

// v77.19 — Custody day-cell tints now derive from each parent's persisted
// `parent_color` so the calendar visually matches Expenses, Messages and
// every other surface. The `tintBg` helper wraps the colour at ~10% opacity
// so events still pop on top. Falls back to the legacy pale-blue/pale-sage
// pair only when there's no schedule wired up yet, purely as a safety net.
const CUSTODY_TINT_FALLBACK_A = "#E6F1F8";  // very pale steel-blue
const CUSTODY_TINT_FALLBACK_B = "#E7F3EA";  // very pale sage
function custodyBgColor(parentId, schedule, members) {
  if (!schedule || !parentId) return null;
  // Resolve the parent's display colour and turn it into a soft tint.
  const c = getParentColorById(parentId, members);
  if (c) return tintBg(c);
  // Defensive fallback — only fires if both lookups are empty.
  if (parentId === schedule.parent_a_id) return CUSTODY_TINT_FALLBACK_A;
  if (parentId === schedule.parent_b_id) return CUSTODY_TINT_FALLBACK_B;
  return null;
}

const typeClass = (t, parentIdx) => {
  if (t === "custody") return parentIdx === 0 ? "custody-a" : "custody-b";
  if (t === "school") return "event-school";
  if (t === "holiday") return "event-holiday";
  if (t === "call" || t === "video_call") return "bg-[#E8ECF3] border-[#4A6FA5] text-[#2E3F66]";
  // v77.99.30 — new event types share colour families with existing
  // peers so the design system stays coherent. Sports = warm orange
  // (energy/movement). Extracurricular = soft purple (creativity).
  if (t === "sports") return "bg-[#FFE9D6] border-[#C76A1F] text-[#7A3A0A]";
  if (t === "extracurricular") return "bg-[#EBE3F5] border-[#6B4FA0] text-[#3A2A66]";
  return "bg-[#EEF3F4] border-[#D9E1E3] text-[#2E3A3F]";
};

// v77.99.30 — Privacy-first defaults across the app.
//
// Calendar events now default to **private** (is_shared: false) for
// EVERY user — parents and mediators alike. Sharing should be an
// explicit, deliberate act, especially in high-conflict cases where
// accidentally surfacing a private appointment to the other parent
// can do real harm. The user can flip the "Share with co-parent"
// toggle in the create/edit dialogs whenever they choose to share.
//
// (The behaviour for mediator-authored events is unchanged — they
// were always private. The breaking change is for parents, who
// previously defaulted to shared.)
//
// v77.99.30 — Also added "extracurricular" + "sports" event types
// alongside the legacy custody/pickup/dropoff/school/holiday/call
// set. Both are styled via `typeClass()` and labelled in every view
// (Month, Week, Day, List, DayDetail, NewEvent, EditOccurrence).
const emptyForm = (_isMediator = false) => ({
  title: "", type: "custody", parent_id: "", child_ids: [], start: "", end: "", notes: "",
  is_shared: false, freq: "none", byweekday: [], until: "", count: "", end_mode: "never",
  reminders: [], custom_minutes: "",
});

export default function CalendarPage() {
  const { user, members, isMediator } = useAuth();
  const { selectedChildId } = useChildScope();
  const navigate = useNavigate();
  // v77.49 — Audit trail for third-party viewers.
  useEffect(() => {
    recordThirdPartyView({ resource_type: "calendar", resource_label: "Opened calendar page" });
  }, []);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => emptyForm(isMediator));
  const [proposeFor, setProposeFor] = useState(null);
  const [proposeChanges, setProposeChanges] = useState({ start: "", end: "", note: "" });
  const [monthOffset, setMonthOffset] = useState(0);
  // v77.99.24 — Week View offset (0 = this week, -1 = last week, etc).
  // Independent of monthOffset because the views are non-overlapping
  // tabs and should remember their own position when the user toggles.
  const [weekOffset, setWeekOffset] = useState(0);
  // v77.99.30 — Day View. `dayOffset` is days from today. We keep this
  // parallel to `weekOffset` rather than deriving it so the user's
  // selected day persists when they switch back and forth from
  // Week/Month/List without losing their place.
  const [dayOffset, setDayOffset] = useState(0);
  const [dayDetail, setDayDetail] = useState(null);
  // v77.99.19 (Phase 3 slice 2) — Calendar filter state moved into
  // a dedicated hook to honour the "preserve child-specific filtering
  // isolation" requirement. State lives next to the predicate that
  // drives the grid so they can never drift.
  const [editOccurrence, setEditOccurrence] = useState(null); // { ev, scope: 'single'|'future'|'series', form }
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  // v77.99.78 — One-click .ics export dialog state. Driven by the
  // "Sync to my calendar" button in the page header.
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  // v77.99.76 — Five independent data fetches (events + active children,
  // custody overlay, maintenance chips, schedule proposals, stale-
  // schedule audit) are bundled into a dedicated hook. Each setter is
  // re-exposed under its legacy name so the rest of the page can keep
  // mutating in place (e.g. splicing into `events` after createEvent
  // rather than re-fetching the whole list).
  //
  // Per-child custody — v77.22. Backend returns:
  //   - legacy fields (`schedule`, `overrides`, `assignments`) for the
  //     household-wide fallback (used for the day-cell tint when no
  //     child filter is active)
  //   - `by_child`: { child_id: { schedule, overrides, assignments,
  //     handover_times, has_schedule, child_name, child_color } }
  // Each child can have a different schedule with different parents.
  //
  // v77.26 — `scheduleProposals` lists every pending schedule-change
  // proposal the viewer can see. Primary parent → approve/reject;
  // proposer → cancel; everyone else → read-only.
  // v77.31 — `scheduleAudit` surfaces stale schedules referencing parents
  // who are no longer linked to a child (data drift fixed by v77.25).
  const {
    events,
    children,
    custody,
    maintenanceChips,
    scheduleProposals,
    scheduleAudit,
    load,
    loadCustody,
    loadProposals,
    loadScheduleAudit,
  } = useCalendarData({ selectedChildId, monthOffset, isMediator });
  // ---- New Event modal: dirty-check & pre-fill ----------------------------
  // Snapshot taken when the new-event dialog opens. We compare against the
  // current `form` to detect unsaved changes for confirm-on-close.
  const newEventSnapshot = useRef("");
  // Drag-and-drop reschedule state. We support both desktop HTML5 drag
  // (mouse) and mobile pointer-based drag (long-press → drag). Recurring
  // events are intentionally NOT draggable — moving a single occurrence
  // versus the whole series is ambiguous and we want the user to use the
  // explicit edit dialog for that.
  const [dragEvent, setDragEvent] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  // Long-press timer ref — used for the cell long-press gesture that
  // opens "New event" prefilled regardless of whether the day already has
  // events. Cleared on pointermove (>10px) or pointerup before threshold.
  const longPressTimer = useRef(null);

  // v77.99.26 — Month View drag-to-extend.
  //
  // Mirror of the Week-View drag-to-resize gesture (v77.99.24/25) adapted
  // for the Month grid: the user long-presses (300ms) the bottom edge of
  // an event chip, drags onto a target day cell, and on release we open
  // EditOccurrenceDialog pre-filled with a new `end` whose DATE is the
  // target cell and whose TIME-of-day is preserved from the original.
  //
  // Design rules (kept tight on purpose):
  //   • Long-press 300ms to ARM — same as Week View. Sub-300ms press
  //     opens the regular edit dialog (existing chip onClick).
  //   • Window-level pointer listeners (same closure-bug fix as Week
  //     View v77.99.25) so cursor leaving the 4-px handle never drops
  //     the gesture mid-drag.
  //   • `document.elementFromPoint(x, y)` + `closest('[data-testid^="day-"]')`
  //     resolves the target cell as the user drags — works regardless
  //     of which child the cursor is over (the chip itself, an icon,
  //     etc).
  //   • Same save-path as Week View: we never PUT directly. The user
  //     lands in EditOccurrenceDialog → recurrence scope picker
  //     (single / future / series) → Save. Audit trail + recurrence
  //     logic stay intact.
  //   • Clamp: if user drags BACKWARDS (cursor day < original start
  //     day), refuse — Month View can only EXTEND, not shorten below
  //     the start. Shortening within a day is a Week-View affordance.
  // v77.99.77 — Month-View drag-to-resize is now owned by `useMonthResize`.
  // The hook keeps the long-press → drag-extend → drop state machine
  // self-contained and exposes the few values the JSX still needs.
  //
  // We pass `openEdit` and `toLocalInput` via refs so the hook can be
  // invoked here (high up in render) before `openEdit` is declared
  // ~290 lines below. The refs are assigned immediately after each
  // callback is declared, so by the time the user actually drag-
  // resizes, both refs are populated.
  const openEditRef = useRef(null);
  const toLocalInputRef = useRef(null);
  const {
    monthResize,
    monthResizeStateRef,
    longPressMonthRef,
    beginMonthResize,
    cancelMonthResize: _cancelMonthResize,
    updateMonthResize: _updateMonthResize,
  } = useMonthResize({ isMediator, openEditRef, toLocalInputRef });
  // `toLocalInput` is a module-level helper (imported at the top of
  // this file) so its ref can be wired immediately. `openEditRef` is
  // wired below, right after `openEdit` is declared.
  toLocalInputRef.current = toLocalInput;

  // v77.99.76 — Data fetches (events + active children, custody overlay,
  // maintenance chips, schedule proposals, stale-schedule audit) live in
  // `useCalendarData` (destructured at the top of this component). The
  // form-pre-fill effect below is the only one that depends on user/form
  // state rather than visible-month state, so it stays local.
  useEffect(() => { if (user?.id && !form.parent_id) setForm((f) => ({ ...f, parent_id: user.id })); }, [user?.id, form.parent_id]);

  const parentIdx = (pid) => Math.max(0, members.findIndex((m) => m.id === pid));
  const childById = useMemo(() => Object.fromEntries(children.map((c) => [c.id, c])), [children]);

  // Compute "handovers" — dates where custody changes hands. Stored as
  // a {iso → {from_parent_id, to_parent_id, reason, handover_time}} map
  // so cell rendering stays O(1).
  //
  // For most patterns the handover marker is attached to the FIRST day
  // of the new parent's window (i.e. when yesterday's custody differs
  // from today's, mark TODAY).
  //
  // For "every_second_weekend" we honour the user's configured weekend
  // window: the "return to parent A" handover physically happens at
  // weekend_end_time on the LAST day of B's window (Sunday at 20:30 in
  // the user's example). So for the B→A transition we attach the
  // marker to the PREVIOUS day (B's last day) rather than to A's first
  // day. The "start of B's window" marker stays on B's first day.
  const handovers = useMemo(() => {
    const out = {};
    const sortedDates = Object.keys(custody.assignments || {}).sort();
    const sched = custody.schedule;
    const isWeekendPattern = sched?.pattern === "every_second_weekend";
    const startTime = sched?.weekend_start_time || "17:00";
    const endTime = sched?.weekend_end_time || "19:00";
    const parentBId = sched?.parent_b_id;
    for (let i = 1; i < sortedDates.length; i++) {
      const prev = sortedDates[i - 1];
      const cur = sortedDates[i];
      // Only treat consecutive days as a handover (ignore gaps).
      const dPrev = new Date(`${prev}T00:00:00`);
      const dCur = new Date(`${cur}T00:00:00`);
      const diff = (dCur - dPrev) / (1000 * 60 * 60 * 24);
      if (diff !== 1) continue;
      const a = custody.assignments[prev];
      const b = custody.assignments[cur];
      if (!a?.parent_id || !b?.parent_id) continue;
      if (a.parent_id === b.parent_id) continue;

      // Schedule-driven transitions on every_second_weekend: split into
      // "A → B" (start of B's window, mark cur) and "B → A" (end of B's
      // window, mark prev).
      const isScheduleTransition = isWeekendPattern && a.source === "schedule" && b.source === "schedule";
      if (isScheduleTransition && a.parent_id === parentBId) {
        // B → A. The handover happens on B's last day (prev) at end_time.
        if (!out[prev]) {
          out[prev] = {
            from_parent_id: a.parent_id,
            to_parent_id: b.parent_id,
            reason: null,
            handover_time: endTime,
          };
        }
        continue;
      }
      // All other cases (start of B's window, override-driven swaps,
      // non-weekend patterns): mark `cur` as the handover day.
      let handover_time = null;
      if (isScheduleTransition && b.parent_id === parentBId) {
        handover_time = startTime;
      }
      out[cur] = {
        from_parent_id: a.parent_id,
        to_parent_id: b.parent_id,
        reason: b.source === "override" ? b.reason : null,
        handover_time,
      };
    }
    return out;
  }, [custody.assignments, custody.schedule]);

  const memberName = (id) => members.find((m) => m.id === id)?.name || "Co-parent";

  // v77.22 — Per-child handovers. Mirrors the legacy `handovers` logic
  // but keyed by child so we can render "Child 1 → Parent 1 to Parent 2
  // at 17:00" instead of one generic line per day. Returns
  //   { iso: [ { child_id, child_name, child_color, from_parent_id,
  //              to_parent_id, handover_time, reason } ] }
  const handoversByChild = useMemo(() => {
    const out = {};
    const byChild = custody.byChild || {};
    Object.values(byChild).forEach((cc) => {
      const sched = cc.schedule;
      const isWeekendPattern = sched?.pattern === "every_second_weekend";
      const startTime = sched?.weekend_start_time || "17:00";
      const endTime = sched?.weekend_end_time || "19:00";
      const parentBId = sched?.parent_b_id;
      const sortedDates = Object.keys(cc.assignments || {}).sort();
      for (let i = 1; i < sortedDates.length; i++) {
        const prev = sortedDates[i - 1];
        const cur = sortedDates[i];
        const dPrev = new Date(`${prev}T00:00:00`);
        const dCur = new Date(`${cur}T00:00:00`);
        if ((dCur - dPrev) / (1000 * 60 * 60 * 24) !== 1) continue;
        const a = cc.assignments[prev];
        const b = cc.assignments[cur];
        if (!a?.parent_id || !b?.parent_id || a.parent_id === b.parent_id) continue;
        const isScheduleTransition = isWeekendPattern && a.source === "schedule" && b.source === "schedule";
        const entry = {
          child_id: cc.child_id,
          child_name: cc.child_name,
          child_color: cc.child_color,
          from_parent_id: a.parent_id,
          to_parent_id: b.parent_id,
          reason: b.source === "override" ? b.reason : null,
          handover_time: null,
        };
        if (isScheduleTransition && a.parent_id === parentBId) {
          // B → A: handover lands on the LAST day of B's window.
          entry.handover_time = endTime;
          (out[prev] = out[prev] || []).push(entry);
          continue;
        }
        if (isScheduleTransition && b.parent_id === parentBId) {
          entry.handover_time = startTime;
        }
        (out[cur] = out[cur] || []).push(entry);
      }
    });
    return out;
  }, [custody.byChild]);

  // v77.22 — Per-child assignment for a given iso date. Returns array of
  // { child_id, child_name, child_color, parent_id, parent_name,
  //   has_schedule, source } so the day-cell + day-detail modal can
  // render "Child · With Parent" rows. Children visible to the user but
  // without a schedule appear with has_schedule=false so the UI can
  // render "No schedule set".
  const childCustodyForDate = useCallback((isoKey) => {
    const out = [];
    Object.values(custody.byChild || {}).forEach((cc) => {
      const a = cc.assignments?.[isoKey];
      const parent = a ? members.find((m) => m.id === a.parent_id) : null;
      out.push({
        child_id: cc.child_id,
        child_name: cc.child_name,
        child_color: cc.child_color,
        parent_id: a?.parent_id || null,
        parent_name: parent?.name || null,
        has_schedule: !!cc.has_schedule,
        source: a?.source || null,
        reason: a?.reason || null,
      });
    });
    return out;
  }, [custody.byChild, members]);

  // v77.10 — Relationship-driven helper. Returns the parents eligible
  // to be assigned to an event covering `child_ids`. Backed by the
  // `parent_ids` field on each child doc (server-side authoritative —
  // primary parent + active `child_memberships` for the child). Falls
  // back to the full household list when no children are picked
  // (e.g. unscoped events) or when children haven't hydrated yet, so
  // the UI never strands the user with an empty dropdown.
  const eligibleParentsFor = (childIds) => {
    if (!childIds || childIds.length === 0) return members;
    const ids = new Set();
    for (const cid of childIds) {
      const child = children.find((c) => c.id === cid);
      for (const pid of (child?.parent_ids || [])) ids.add(pid);
    }
    if (ids.size === 0) return members;
    return members.filter((m) => ids.has(m.id) || m.is_placeholder);
  };

  const buildRecurrence = (f) => {
    if (!f.freq || f.freq === "none") return null;
    const rec = { freq: f.freq };
    if ((f.freq === "weekly" || f.freq === "fortnightly") && f.byweekday.length > 0) rec.byweekday = f.byweekday;
    if (f.end_mode === "on_date" && f.until) rec.until = f.until;
    else if (f.end_mode === "after_count" && f.count) rec.count = Math.max(1, parseInt(f.count, 10));
    // "never" -> no until, no count (backend uses 365-day safety horizon)
    return rec;
  };

  const parseReminderOffsets = (form) => {
    const offs = [...(form.reminders || [])];
    const custom = (form.custom_minutes || "").toString().trim();
    if (custom) {
      const n = parseInt(custom, 10);
      if (!Number.isNaN(n) && n > 0 && !offs.includes(n)) offs.push(n);
    }
    return offs.filter((n) => Number.isInteger(n) && n > 0).sort((a, b) => a - b);
  };

  const createEvent = async (e) => {
    e.preventDefault();
    // Phase 3 Delivery B — explicit child selection on event create
    // when the household has children.
    if ((children || []).length > 0 && (!form.child_ids || form.child_ids.length === 0)) {
      toast.error("Please pick at least one child for this event.");
      return;
    }
    try {
      const payload = {
        title: form.title, type: form.type, parent_id: form.parent_id || null,
        child_ids: form.child_ids, start: fromLocalInput(form.start),
        end: form.end ? fromLocalInput(form.end) : fromLocalInput(form.start),
        notes: form.notes, is_shared: form.is_shared, recurrence: buildRecurrence(form),
      };
      const { data: created } = await api.post("/events", payload);
      // Save per-user reminders (if any) against the created event
      const offsets = parseReminderOffsets(form);
      if (offsets.length > 0 && created?.id) {
        await api.put(`/events/${created.id}/reminders`, { offsets });
      }
      toast.success("Event added");
      setOpen(false);
      setForm({ ...emptyForm(isMediator), parent_id: user?.id || "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const submitProposal = async () => {
    try {
      const proposed = {};
      if (proposeChanges.start) proposed.start = fromLocalInput(proposeChanges.start);
      if (proposeChanges.end) proposed.end = fromLocalInput(proposeChanges.end);
      if (!proposed.start && !proposed.end && !proposeChanges.note) {
        toast.error("Please propose a change (start, end, or a note)");
        return;
      }
      // v77.99.18 — Idempotency-Key on schedule-change proposal.
      await api.post(
        "/requests",
        {
          type: "schedule_change",
          target_event_id: proposeFor.id,
          proposed,
          message: proposeChanges.note || "",
        },
        { headers: idempotencyHeader(generateIdempotencyKey()) },
      );
      toast.success("Change request sent — your co-parent will review it");
      setProposeFor(null);
      setProposeChanges({ start: "", end: "", note: "" });
      setDayDetail(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const approve = async (id) => {
    try { await api.post(`/events/${id}/approve`); toast.success("Agreed"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const reject = async (id) => {
    try { await api.post(`/events/${id}/reject`); toast.success("Not agreed"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // Navigate to Journal filtered to this event's linked entries.
  // Used by the small "{n} linked journal entr(y|ies)" pill.
  const openLinkedJournal = (ev) => {
    setDayDetail(null);
    setEditOccurrence(null);
    navigate(`/app/journal?eventId=${encodeURIComponent(ev.id)}`);
  };

  // Navigate to Journal with a prefilled new-entry payload derived from
  // this calendar event. Defaults the entry to private; the user may
  // toggle Share before saving. Closes the day-detail sheet (if open) so
  // the user lands on Journal cleanly.
  const openJournalNote = (ev) => {
    const prefill = buildJournalPrefillFromEvent(ev, members, children, user);
    if (!prefill) return;
    setDayDetail(null);
    navigate("/app/journal", { state: { prefillFromEvent: prefill } });
  };

  // Export this event as a .ics calendar file. On mobile (Web Share
  // supported) the OS share sheet routes it straight into Apple
  // Calendar, Google Calendar, Outlook, etc. On desktop it downloads
  // and the user double-clicks to import.
  const exportEventToCalendar = (ev) => {
    const safe = (ev.title || "event").replace(/[^a-zA-Z0-9-_]/g, "_").slice(0, 40) || "event";
    downloadFile(
      `${API}/events/${encodeURIComponent(ev.id)}/ics`,
      `${safe}.ics`,
      { kind: "ics", label: "Generating calendar file" },
    );
  };

  const openEdit = async (ev, formOverrides = {}) => {
    // v77.99.99 — Contact Visit events surfaced in the calendar are
    // read-only here. Route them to the Contact Visits page where the
    // full record (timeline, notes, evidence reference, lifecycle
    // actions) lives. The query param auto-opens the detail panel.
    if (ev?.kind === "contact_visit") {
      navigate(`/app/contact-visits?visit=${ev.visit_id || ev.id?.replace(/^cv-/, "")}`);
      return;
    }
    const isOccurrence = !!ev.series_id;
    const seriesId = ev.series_id || ev.id;
    const base = events.find((e) => e.id === seriesId) || ev;
    const rec = base.recurrence || {};
    let end_mode = "never";
    if (rec.until) end_mode = "on_date";
    else if (rec.count) end_mode = "after_count";
    // Fetch my reminders for this event (series or occurrence)
    let offsets = [];
    try {
      const { data } = await api.get(`/events/${ev.id}/reminders`);
      offsets = data.offsets || [];
    } catch (err) {
      // Personal reminders are optional; a missing feed isn't a blocker for opening the edit dialog.
      console.warn("Failed to load event reminders:", err?.response?.status || err?.message);
    }
    setEditOccurrence({
      ev,
      seriesId,
      occurrenceDate: ev.occurrence_date || null,
      isRecurring: !!base.recurrence,
      scope: isOccurrence ? "single" : "series",
      form: {
        title: ev.title, type: ev.type, parent_id: ev.parent_id || "",
        child_ids: ev.child_ids || [],
        start: toLocalInput(ev.start), end: toLocalInput(ev.end),
        notes: ev.notes || "", is_shared: !!ev.is_shared,
        freq: rec.freq || "none", byweekday: rec.byweekday || [],
        until: rec.until || "", count: rec.count || "", end_mode,
        reminders: offsets, custom_minutes: "",
        // v77.99.25 — Optional form patch from callers like the
        // Week-View drag-to-resize handle. Lets the caller pre-fill
        // `end` (or any other form field) so the user lands in the
        // edit dialog with their resize already applied and only
        // needs to choose the recurrence scope.
        ...formOverrides,
      },
    });
  };
  // v77.99.77 — Bind the openEdit callback to its ref now that the
  // declaration above is in scope. `useMonthResize` consumes the ref
  // lazily from inside its drop handler, so this single assignment
  // per render is enough — no useEffect needed.
  openEditRef.current = openEdit;

  const submitEdit = async () => {
    const { ev, seriesId, occurrenceDate, scope, form: f } = editOccurrence;
    try {
      if (scope === "single") {
        const updates = {
          title: f.title, type: f.type, parent_id: f.parent_id || null,
          child_ids: f.child_ids, notes: f.notes, is_shared: f.is_shared,
          start: fromLocalInput(f.start), end: fromLocalInput(f.end),
        };
        await api.patch(`/events/${seriesId}/occurrence`, { scope: "single", occurrence_date: occurrenceDate, cancel: false, updates });
        toast.success("Occurrence updated");
      } else if (scope === "future") {
        const updates = {
          title: f.title, type: f.type, parent_id: f.parent_id || null,
          child_ids: f.child_ids, notes: f.notes, is_shared: f.is_shared,
          start: fromLocalInput(f.start), end: fromLocalInput(f.end),
          recurrence: buildRecurrence(f),
        };
        await api.post(`/events/${seriesId}/split-series`, { occurrence_date: occurrenceDate, updates });
        toast.success("This and future occurrences updated");
      } else {
        const payload = {
          title: f.title, type: f.type, parent_id: f.parent_id || null,
          child_ids: f.child_ids,
          start: fromLocalInput(f.start),
          end: f.end ? fromLocalInput(f.end) : fromLocalInput(f.start),
          notes: f.notes, is_shared: f.is_shared, recurrence: buildRecurrence(f),
        };
        await api.put(`/events/${seriesId}`, payload);
        toast.success("Series updated");
      }
      // Save my reminders for this event/occurrence (my private setting)
      const offsets = parseReminderOffsets(f);
      const reminderId = scope === "single" && occurrenceDate ? `${seriesId}@${occurrenceDate}` : seriesId;
      await api.put(`/events/${reminderId}/reminders`, { offsets, occurrence_date: scope === "single" ? occurrenceDate : null });
      setEditOccurrence(null);
      setDayDetail(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const cancelOccurrence = async () => {
    const { seriesId, occurrenceDate } = editOccurrence;
    try {
      await api.patch(`/events/${seriesId}/occurrence`, { scope: "single", occurrence_date: occurrenceDate, cancel: true });
      toast.success("Occurrence cancelled");
      setEditOccurrence(null);
      setDayDetail(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  // ---- New Event modal lifecycle -----------------------------------------
  // Called both by the explicit "Add event" button and by tap/long-press on
  // a calendar cell. Pre-fills start (and end + 1h) when a date is supplied.
  const openCreate = (date, startTime = null) => {
    const next = { ...emptyForm(isMediator), parent_id: user?.id || "" };
    // Phase 3 Delivery B — pre-fill child_ids from the active scope so
    // single-child users don't have to re-pick. In master view this stays
    // empty and the user must tap a child chip explicitly.
    if (selectedChildId) next.child_ids = [selectedChildId];
    if (date || startTime) {
      // v77.99.24 — When the WeekView passes a `startTime` Date the user
      // tapped on the grid, honour their snapped time. Otherwise default
      // to 09:00 on the picked day, consistent with the Month view tap.
      const start = startTime ? new Date(startTime) : new Date(date);
      if (!startTime) start.setHours(9, 0, 0, 0);
      const end = new Date(start.getTime() + 60 * 60 * 1000); // +1h block
      next.start = toLocalInput(start.toISOString());
      next.end = toLocalInput(end.toISOString());
    }
    setForm(next);
    newEventSnapshot.current = JSON.stringify(next);
    setOpen(true);
  };

  const isNewEventDirty = () => JSON.stringify(form) !== newEventSnapshot.current;

  const tryCloseNewEvent = () => {
    if (isNewEventDirty() && !window.confirm("You have unsaved changes. Discard them?")) return;
    setOpen(false);
    setForm({ ...emptyForm(isMediator), parent_id: user?.id || "" });
  };

  // ---- Drag-and-drop reschedule ------------------------------------------
  // Backend already supports moving an event via PUT /events/{id} (full
  // series replace) and PATCH /events/{seriesId}/occurrence (single
  // occurrence). For drag-drop we only allow non-recurring events to keep
  // the UX unambiguous; recurring events open the explicit edit dialog so
  // the user can choose scope (single / future / series).
  const moveEventToDate = async (ev, targetDate) => {
    if (!ev || !targetDate) return;
    const oldStart = new Date(ev.start);
    const oldEnd = new Date(ev.end || ev.start);
    const target = new Date(targetDate);
    target.setHours(oldStart.getHours(), oldStart.getMinutes(), 0, 0);
    if (Math.floor(oldStart.getTime() / 86400000) === Math.floor(target.getTime() / 86400000)) return; // same day no-op
    const durationMs = oldEnd.getTime() - oldStart.getTime();
    const newEnd = new Date(target.getTime() + Math.max(0, durationMs));
    try {
      await api.put(`/events/${ev.id}`, {
        title: ev.title,
        type: ev.type,
        parent_id: ev.parent_id || null,
        child_ids: ev.child_ids || [],
        start: target.toISOString(),
        end: newEnd.toISOString(),
        notes: ev.notes || "",
        is_shared: !!ev.is_shared,
        recurrence: null,
      });
      toast.success(`Event moved to ${target.toLocaleDateString()}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not move event");
    }
  };

  // Long-press (mobile + desktop) — uses pointer events to support both.
  // 600ms hold = long-press; quick release = tap. Movement >10px cancels.
  // Returns props you can spread onto a clickable element.
  const longPressProps = (onTap, onLong) => ({
    onPointerDown: (e) => {
      const startX = e.clientX, startY = e.clientY;
      if (longPressTimer.current) clearTimeout(longPressTimer.current);
      longPressTimer.current = setTimeout(() => {
        longPressTimer.current = null;
        onLong?.();
      }, 600);
      const moveHandler = (ev2) => {
        if (Math.abs(ev2.clientX - startX) + Math.abs(ev2.clientY - startY) > 10) {
          if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
          cleanup();
        }
      };
      const upHandler = () => {
        if (longPressTimer.current) {
          clearTimeout(longPressTimer.current);
          longPressTimer.current = null;
          onTap?.();
        }
        cleanup();
      };
      const cleanup = () => {
        window.removeEventListener("pointermove", moveHandler);
        window.removeEventListener("pointerup", upHandler);
        window.removeEventListener("pointercancel", cleanup);
      };
      window.addEventListener("pointermove", moveHandler);
      window.addEventListener("pointerup", upHandler);
      window.addEventListener("pointercancel", cleanup);
    },
  });

  // Apply the legend filters. Two independent filter sets (children +
  // parents) combined with AND semantics. When no chip is selected for
  // a row, that row is a no-op pass-through. Privacy guardrail: when a
  // selected parent is NOT the current user, only their mutually-shared
  // events are kept — private entries never leak through the filter.
  const visibleEventsHook = useCalendarFilters(events, selectedChildId, user?.id);
  const {
    childFilter, setChildFilter,
    parentFilter, setParentFilter,
    visibleEvents,
  } = visibleEventsHook;

  // Month grid
  const monthData = useMemo(() => {
    const now = new Date();
    const viewDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const startWeekday = (first.getDay() + 6) % 7;
    const daysInMonth = last.getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);

    const byDay = {};
    visibleEvents.forEach((ev) => {
      const s = new Date(ev.start);
      const key = `${s.getFullYear()}-${s.getMonth()}-${s.getDate()}`;
      (byDay[key] = byDay[key] || []).push(ev);
    });
    return { viewDate, cells, byDay, label: viewDate.toLocaleDateString(undefined, { year: "numeric", month: "long" }) };
  }, [visibleEvents, monthOffset]);

  const toggleChildInForm = (cid, setter, current) => {
    const list = current.includes(cid) ? current.filter((x) => x !== cid) : [...current, cid];
    setter(list);
  };

  const childSwatches = (ids) => (ids || [])
    .map((id) => childById[id])
    .filter(Boolean);

  // Parenting schedule legend strip — shown above the grid when a schedule
  // exists. Helps the user instantly read the day-cell tints. We also
  // surface a "Set up" prompt when no schedule exists yet, so the feature
  // is discoverable.
  const sched = custody.schedule;
  // v77.22 — When a single child is selected, expose THAT child's
  // schedule for the legend (parents may differ across children). In
  // master "All children" view we deliberately omit the household-wide
  // Parent A / Parent B legend per the spec: "Do not show Parent A vs
  // Parent B in All children view".
  // v77.22.6 — Honour the calendar's local chip-filter as well: a
  // single-child chip filter should drive the legend the same way
  // the global child-scope dropdown does.
  const scopedChildIdForLegend = selectedChildId || (childFilter.size === 1 ? Array.from(childFilter)[0] : null);
  const activeChildSchedule = useMemo(() => {
    if (!scopedChildIdForLegend) return null;
    return custody.byChild?.[scopedChildIdForLegend]?.schedule || null;
  }, [scopedChildIdForLegend, custody.byChild]);
  const legendSched = activeChildSchedule || (scopedChildIdForLegend ? null : sched);
  const showParentLegend = !!legendSched && (
    // Always show when filtering to a single child (their own parents).
    !!scopedChildIdForLegend ||
    // In All-children view, only show when the household has at most ONE
    // schedule overall (i.e. the "simple" 2-parent household). Hide as
    // soon as multiple per-child schedules exist with differing parent
    // pairs — that's the case the user explicitly called out.
    (() => {
      const schedules = Object.values(custody.byChild || {})
        .map((cc) => cc.schedule)
        .filter(Boolean);
      const pairs = new Set(schedules.map((s) => `${s.parent_a_id}|${s.parent_b_id}`));
      return pairs.size <= 1;
    })()
  );
  const parentAName = legendSched ? (members.find((m) => m.id === legendSched.parent_a_id)?.name || "Parent A") : null;
  const parentBName = legendSched ? (members.find((m) => m.id === legendSched.parent_b_id)?.name || "Parent B") : null;

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.12em] text-[#6F828A]">Shared calendar</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium mt-1">Calendar</h1>
          <p className="text-sm text-[#6F828A] mt-1">Private by default. Share with your co-parent when ready. Changes go through approvals.</p>
        </div>
        {/* v77.99.70 — Mediator-only "Download read-only PDF" button.
            Routes to the Court Bundle page where the mediator can
            select a date window + sections and generate a full PDF. */}
        <MediatorPdfDownloadButton
          to="/app/court-bundle"
          label="Download read-only PDF"
          testid="calendar-mediator-pdf"
          className="flex-shrink-0"
        />
        {!isMediator && (
          <NewEventDialog
            open={open}
            form={form}
            setForm={setForm}
            childList={children}
            eligibleParentsFor={eligibleParentsFor}
            toggleChildInForm={toggleChildInForm}
            openCreate={openCreate}
            tryCloseNewEvent={tryCloseNewEvent}
            isNewEventDirty={isNewEventDirty}
            createEvent={createEvent}
          />
        )}
        {/* v77.99.78 — One-click .ics export for Google / Apple Calendar
            / Outlook. Lives next to the "Add event" button so the user
            sees it whenever they're already thinking about their
            calendar. Hidden for mediators (they have the read-only PDF
            instead, surfaced above). */}
        {!isMediator && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setExportDialogOpen(true)}
            className="border-[#D9E1E3] text-[#116B6E] hover:bg-[#F0F8F8]"
            data-testid="calendar-export-ics-btn"
            title="Export to Google Calendar / Apple Calendar / Outlook"
          >
            <Download className="w-4 h-4 mr-2" /> Sync to my calendar
          </Button>
        )}
      </div>

      <div className="space-y-1.5" data-testid="calendar-legend">
        <div className="flex flex-wrap gap-2 text-xs items-center" data-testid="calendar-legend-parents">
          {showParentLegend && legendSched && (
            <>
              <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Parenting:</span>
              {[
                { id: legendSched.parent_a_id, name: parentAName, testid: "legend-parent-a" },
                { id: legendSched.parent_b_id, name: parentBName, testid: "legend-parent-b" },
              ].map((p) => {
                const active = parentFilter.has(p.id);
                const isSelf = p.id === user?.id;
                // v77.19 — Each legend swatch picks up that parent's
                // own persisted display colour. Border uses the solid
                // hue, background uses the tinted version so the legend
                // matches the day-cell custody overlay exactly.
                const parentColor = getParentColorById(p.id, members);
                return (
                  <button
                    type="button"
                    key={p.id || p.testid}
                    onClick={() => setParentFilter((prev) => {
                      const next = new Set(prev);
                      if (next.has(p.id)) next.delete(p.id); else next.add(p.id);
                      return next;
                    })}
                    aria-pressed={active}
                    title={active
                      ? `Filtering by ${p.name} — tap to remove`
                      : isSelf
                        ? `Tap to filter by ${p.name} (you)`
                        : `Tap to filter by ${p.name} — only mutually-shared events`}
                    className={`px-2 py-1 border rounded-sm transition-colors ${active ? "ring-1 ring-[#1F9FA4]" : "hover:brightness-95"}`}
                    style={{ backgroundColor: tintBg(parentColor), borderColor: active ? "#1F9FA4" : parentColor, color: "#2E3A3F" }}
                    data-testid={p.testid}
                    data-active={active ? "true" : "false"}
                  >
                    {p.name}
                  </button>
                );
              })}
            </>
          )}
          {!sched && !isMediator && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setScheduleDialogOpen(true)}
              className="border-dashed border-[#A7D8DA] text-[#116B6E] bg-[#F0F8F8] hover:bg-[#E1F0F0] rounded-sm h-7 text-[11px]"
              data-testid="setup-parenting-schedule-btn"
            >
              <Plus className="w-3 h-3 mr-1" /> Set up parenting schedule
            </Button>
          )}
          {sched && !isMediator && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setScheduleDialogOpen(true)}
              className="h-7 text-[11px] text-[#6F828A] hover:bg-[#EEF3F4]"
              data-testid="edit-parenting-schedule-btn"
            >
              Edit schedule
            </Button>
          )}
          {/* v77.22 — Schedule coverage pill. Shown only when there's at
              least one child WITHOUT a per-child schedule (and there
              are 2+ accessible children — single-child households don't
              need this nudge). Tapping opens the schedule dialog so
              the primary parent can fill in the gap. The pill is
              quiet/grey by default; turns warm amber when ZERO children
              have ANY schedule (legacy or per-child) so the gap reads
              clearly without being preachy. */}
          {!isMediator && custody.parentingScheduleStatus !== "none_currently" && !!sched && (() => {
            const visibleChildren = Object.values(custody.byChild || {});
            if (visibleChildren.length < 2) return null;
            const withOwn = visibleChildren.filter((cc) => cc.has_schedule && !cc.schedule_is_legacy_fallback);
            const withAny = visibleChildren.filter((cc) => cc.has_schedule);
            const missing = visibleChildren.length - withOwn.length;
            if (missing === 0) return null;
            const noneAtAll = withAny.length === 0;
            const allOnFallback = withOwn.length === 0 && withAny.length === visibleChildren.length;
            // When everyone shares the legacy fallback we keep the pill
            // muted — that's a perfectly valid setup and we don't want
            // to nag a 2-parent / multi-child household into doing
            // unnecessary per-child setup. Only nudge harder when the
            // gap is real (one child has its own schedule and another
            // doesn't, OR no schedule exists anywhere).
            const tone = noneAtAll
              ? "border-[#E6CFA2] bg-[#FFF6E1] text-[#8F6B1F] hover:bg-[#FFEDC1]"
              : allOnFallback
                ? "border-[#D9E1E3] bg-white text-[#6F828A] hover:bg-[#F5F7F8]"
                : "border-[#E6CFA2] bg-[#FFF6E1] text-[#8F6B1F] hover:bg-[#FFEDC1]";
            const label = noneAtAll
              ? `No parenting schedule yet · ${visibleChildren.length} ${visibleChildren.length === 1 ? "child" : "children"}`
              : allOnFallback
                ? `${withAny.length} of ${visibleChildren.length} on shared schedule`
                : `${withOwn.length} of ${visibleChildren.length} ${visibleChildren.length === 1 ? "child" : "children"} have own schedule`;
            return (
              <button
                type="button"
                onClick={() => setScheduleDialogOpen(true)}
                className={`text-[11px] px-2 py-0.5 border rounded-sm inline-flex items-center gap-1.5 transition-colors ${tone}`}
                title={noneAtAll
                  ? "No parenting schedule has been set up yet. Tap to add one."
                  : allOnFallback
                    ? "Every child currently follows the household-wide schedule. Tap to set a per-child schedule for any of them."
                    : `${missing} ${missing === 1 ? "child is" : "children are"} still using the shared schedule. Tap to set them up individually.`}
                data-testid="schedule-coverage-pill"
              >
                <Users className="w-3 h-3" strokeWidth={1.75} />
                <span>{label}</span>
              </button>
            );
          })()}
        </div>
        {/* v77.56 — Calendar child-chip legend hides whenever the
            global ChildScope is already locked to a single child.
            The page is already child-specific in that mode (events
            fetch via `child_id=…`), so a per-child filter row is
            redundant and confusing. Only show it in "All children"
            mode where filtering by one or more children is genuinely
            useful. */}
        {children.length > 0 && !selectedChildId && (
          <div className="flex flex-wrap gap-2 text-xs items-center" data-testid="calendar-legend-children">
            <span className="text-[10px] uppercase tracking-[0.1em] text-[#6F828A]">Children:</span>
            {children.map((c) => {
              const active = childFilter.has(c.id);
              return (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => setChildFilter((prev) => {
                    const next = new Set(prev);
                    if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
                    return next;
                  })}
                  aria-pressed={active}
                  title={active ? `Filtering by ${c.name} — tap to remove` : `Tap to filter by ${c.name}`}
                  className={`px-2 py-1 border rounded-sm inline-flex items-center gap-1.5 transition-colors ${
                    active
                      ? "bg-[#E8F0F1] border-[#1F9FA4] text-[#116B6E] ring-1 ring-[#1F9FA4]"
                      : "bg-white border-[#D9E1E3] text-[#2E3A3F] hover:bg-[#F5F7F8]"
                  }`}
                  data-testid={`legend-child-${c.id}`}
                  data-active={active ? "true" : "false"}
                >
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color || "#1F9FA4" }}></span>
                  {c.name}
                </button>
              );
            })}
          </div>
        )}
        {(childFilter.size > 0 || parentFilter.size > 0) && (
          <div className="flex">
            <button
              type="button"
              onClick={() => { setChildFilter(new Set()); setParentFilter(new Set()); }}
              className="text-[11px] text-[#6F828A] underline-offset-2 hover:underline hover:text-[#2E3A3F]"
              data-testid="legend-clear-filter"
            >
              Clear filter
            </button>
          </div>
        )}
      </div>

      {/* v77.26 — Pending schedule change proposals banner. Shows
          actionable rows: primary parent gets approve/reject; the
          proposer gets a cancel option; everyone else sees a
          read-only pending pill. */}
      {scheduleProposals.length > 0 && (
        <ScheduleProposalsBanner
          proposals={scheduleProposals}
          isPrimaryParent={!!user?.is_primary_parent}
          currentUserId={user?.id}
          members={members}
          childList={children}
          onChanged={() => { loadCustody(); loadProposals(); }}
        />
      )}

      {/* iter 126.c — Custody swap requests inbox + creator. Only
          rendered when a schedule exists (otherwise there's nothing
          to swap against) and the caller is a real parent (mediators
          use the audit trail instead). Placed above the empty-state
          card so an actively-scheduling household sees the inbox
          before drilling into schedule details. */}
      {!isMediator && !!sched && (
        <CustodySwapRequestsCard
          members={members}
          placeholder={(members || []).find((m) => m.is_placeholder) || null}
          currentUserId={user?.id}
          childList={children}
        />
      )}

      {/* v77.99.43 — Prominent empty-state CTA when no parenting
          schedule has been set up yet. This is the single biggest
          UX issue users reported: they don't realise a Schedule is
          its own thing (separate from one-off Custody events). The
          card disappears once any schedule exists — it never nags.
          v77.99.50 — Also hidden when the household has explicitly
          opted out via parenting_schedule_status="none_currently". */}
      {!sched && !isMediator && custody.parentingScheduleStatus !== "none_currently" && (
        <div className="rounded-md border border-[#A7D8DA] bg-gradient-to-br from-[#F0F8F8] to-[#E0F2F2] p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="parenting-schedule-empty-state">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <Repeat className="w-4 h-4 text-[#116B6E] flex-shrink-0" strokeWidth={1.7} />
              <h3 className="font-heading text-base text-[#0B2E32]">No parenting schedule set up yet</h3>
            </div>
            <p className="text-[12.5px] text-[#0F5658] mt-1 leading-relaxed">
              Create your recurring custody arrangement once (e.g. <span className="font-medium">alternating weeks</span> or <span className="font-medium">every-second-weekend</span>) and your calendar will fill in handovers automatically.
            </p>
            <p className="text-[11px] text-[#116B6E] mt-1">
              For temporary changes or one-off swaps, use <span className="font-medium">New event → Custody change</span> instead.
            </p>
            {/* v77.99.50 — Secondary path. Single click marks the
                household as "no current schedule" so the prompt
                stops re-appearing. */}
            <button
              type="button"
              onClick={async () => {
                try {
                  await api.post("/custody/status", { status: "none_currently" });
                  await loadCustody();
                  toast.success(
                    "Noted — we won't prompt you to set up a schedule. You can revisit this any time from the small Parenting schedule line at the top of Calendar if your arrangements change.",
                    { duration: 7000 },
                  );
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Couldn't save that — try again.");
                }
              }}
              className="text-xs text-[#116B6E] underline-offset-2 hover:underline mt-2"
              data-testid="parenting-schedule-empty-state-none-link"
            >
              I don&apos;t have a current parenting schedule
            </button>
          </div>
          <Button
            type="button"
            onClick={() => setScheduleDialogOpen(true)}
            className="bg-[#116B6E] hover:bg-[#0F5658] text-white w-full sm:w-auto flex-shrink-0"
            data-testid="parenting-schedule-empty-state-cta"
          >
            <Plus className="w-4 h-4 mr-2" /> Set up parenting schedule
          </Button>
        </div>
      )}

      {/* v77.99.52 — "No current parenting schedule" is a valid,
          intentional configuration state — not an unfinished setup.
          When the household has opted out we replace the (previously
          prominent) status card with a single quiet line so the
          Calendar doesn't keep feeling like it's in an incomplete
          state. The line is informational only, no warning tonality,
          and offers a small inline "Set up parenting schedule" link
          for users whose arrangements change. */}
      {!sched && !isMediator && custody.parentingScheduleStatus === "none_currently" && (
        <div
          className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-[#6F828A] px-3 py-2 rounded-sm border border-[#EEF3F4] bg-[#FAFBFB]"
          data-testid="parenting-schedule-status-row"
        >
          <Repeat className="w-3.5 h-3.5 text-[#6F828A] flex-shrink-0" strokeWidth={1.7} />
          <span>
            <span className="text-[#2E3A3F] font-medium">Parenting schedule:</span> None currently active
          </span>
          <button
            type="button"
            onClick={async () => {
              try {
                await api.post("/custody/status", { status: null });
                await loadCustody();
                setScheduleDialogOpen(true);
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Couldn't update status — try again.");
              }
            }}
            className="ml-auto text-[#116B6E] hover:underline underline-offset-2 text-[12px]"
            data-testid="parenting-schedule-status-row-cta"
          >
            Set up parenting schedule
          </button>
        </div>
      )}

      {/* v77.31 — Schedule data audit banner. Surfaces stale schedules
          (parent IDs no longer linked to the child) + pre-v77.22
          legacy household-wide schedules, with a one-click "Reset"
          action that hits the existing DELETE /custody/schedule. */}
      {(scheduleAudit.stale.length > 0 || scheduleAudit.legacy_household_wide.length > 0) && (
        <ScheduleAuditBanner
          audit={scheduleAudit}
          isPrimaryParent={!!user?.is_primary_parent}
          onChanged={() => { loadCustody(); loadScheduleAudit(); }}
        />
      )}

      <Tabs defaultValue="month" className="w-full">
        <TabsList className="bg-[#E8F0F1] rounded-sm self-start">
          <TabsTrigger value="month" data-testid="tab-month-view">Month</TabsTrigger>
          <TabsTrigger value="week" data-testid="tab-week-view">Week</TabsTrigger>
          <TabsTrigger value="day" data-testid="tab-day-view">Day</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list-view">List</TabsTrigger>
        </TabsList>

        {/* v77.99.24 — Week View. Renders the 7 days containing
            "today + weekOffset weeks" as a 16-hour time-grid where
            event height visualises duration. Same data feed as the
            Month view (visibleEvents), same callbacks for open/edit,
            so child-scope filters, permissions, recurrence and
            overrides all flow through unchanged. */}
        <TabsContent value="week" className="mt-4 data-[state=inactive]:hidden">
          <WeekView
            weekOffset={weekOffset}
            setWeekOffset={setWeekOffset}
            visibleEvents={visibleEvents}
            typeClass={typeClass}
            parentIdx={parentIdx}
            childSwatches={childSwatches}
            onEventClick={(ev, day) => setDayDetail({ date: day, events: [ev] })}
            onEmptySlotClick={(start) => openCreate(start, start)}
            // v77.99.25 — Drag-to-resize handoff.
            // The Week View hands us the original event + a new end
            // time (already snapped to 15 min). We open the existing
            // EditOccurrenceDialog with the new end pre-filled so the
            // recurrence-scope picker (single / future / series)
            // remains the ONE save path. No new audit hooks, no risk
            // of bypassing recurrence rules. The user confirms and
            // hits Save — same flow as any other edit.
            onEventResize={(ev, newEndIso) => {
              openEdit(ev, { end: toLocalInput(newEndIso) });
            }}
            isMediator={isMediator}
          />
        </TabsContent>

        {/* v77.99.30 — Day View. Single-day variant of the Week View
            grid. Shares the same time window (06:00-22:00), 15-min
            snap, click-to-add empty slot, and live "now" indicator
            with "Jump to now" pill. Out-of-window events show in a
            small rail at the top so events at 23:00 / 05:00 stay
            discoverable without expanding the grid. Honours child
            filtering + privacy via the same `visibleEvents` feed. */}
        <TabsContent value="day" className="mt-4 data-[state=inactive]:hidden">
          <DayView
            anchor={(() => {
              const a = new Date();
              a.setDate(a.getDate() + dayOffset);
              return a;
            })()}
            events={visibleEvents}
            onPrev={() => setDayOffset(dayOffset - 1)}
            onNext={() => setDayOffset(dayOffset + 1)}
            onToday={() => setDayOffset(0)}
            onEventClick={(ev) => openEdit(ev)}
            onSlotClick={(slotDate) => openCreate(slotDate, slotDate)}
            typeClass={typeClass}
            parentIdx={parentIdx}
            isMediator={isMediator}
          />
        </TabsContent>

        <TabsContent value="month" className="mt-4 data-[state=inactive]:hidden">
          <div className="border border-[#D9E1E3] bg-white rounded-md overflow-hidden" data-testid="month-grid">
            <div className="flex items-center justify-between p-3 border-b border-[#D9E1E3]">
              <Button variant="ghost" size="sm" onClick={() => setMonthOffset(monthOffset - 1)} data-testid="month-prev"><ChevronLeft className="w-4 h-4" /></Button>
              <div className="font-heading text-base font-medium" data-testid="month-label">{monthData.label}</div>
              <Button variant="ghost" size="sm" onClick={() => setMonthOffset(monthOffset + 1)} data-testid="month-next"><ChevronRight className="w-4 h-4" /></Button>
            </div>
            <div className="grid grid-cols-7 text-xs uppercase tracking-[0.08em] text-[#6F828A] border-b border-[#D9E1E3] bg-[#EEF3F4]">
              {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => <div key={d} className="px-2 py-2 text-center">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 auto-rows-[100px]">
              {monthData.cells.map((d, idx) => {
                if (!d) return <div key={`empty-${idx}`} className="border-r border-b border-[#D9E1E3] bg-[#F5F7F8]"></div>;
                const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                const dayEvents = monthData.byDay[key] || [];
                const isToday = new Date().toDateString() === d.toDateString();
                // v77.99.61 — Subtle weekend tint so the rhythm of the
                // week reads at a glance. Only applied when no custody
                // tint, no drag-over highlight and not today; those
                // states take precedence visually.
                const dayOfWeek = d.getDay();
                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                const isDragOver = dragOverKey === key;
                // v77.99.26 — Month View drag-to-extend visual feedback.
                // Highlight the target cell as the user drags. Same
                // styling as `isDragOver` so users get familiar feedback.
                const isMonthResizeOver = monthResize?.cursorDayKey === key;
                // Custody overlay — subtle background tint per assigned parent.
                // We use very low-saturation pastel tints so event chips
                // remain the visual focus.
                const isoKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                // v77.22.6 — Single-child scope is detected from EITHER
                // the global ChildScope picker (selectedChildId) OR the
                // calendar's local chip filter (childFilter Set with
                // exactly one entry). When in single-child mode the
                // day tint and handover badges must reflect THAT
                // child's schedule only, not the legacy household-wide
                // one. Master / multi-child mode falls back to the
                // legacy household-wide assignment for the tint.
                const scopedChildId = selectedChildId || (childFilter.size === 1 ? Array.from(childFilter)[0] : null);
                const childCustody = scopedChildId ? (custody.byChild?.[scopedChildId] || null) : null;
                const childAssignment = childCustody?.assignments?.[isoKey];
                const childSchedule = childCustody?.schedule;
                const assignment = childAssignment || custody.assignments[isoKey];
                const tintSched = childSchedule || custody.schedule;
                const custodyTint = assignment ? custodyBgColor(assignment.parent_id, tintSched, members) : null;
                // Per-child handover indicator: when a single child is
                // scoped, only show ITS handover. Multi-child mode
                // shows them all with the ×N counter.
                const handoversForCell = scopedChildId
                  ? (handoversByChild[isoKey] || []).filter((h) => h.child_id === scopedChildId)
                  : (handoversByChild[isoKey] || []);
                // Tap rule (per spec):
                //   • Empty day  → New event prefilled with that date
                //   • Day with events → Day-detail bottom sheet/modal
                //   • Long-press on ANY day → New event (always)
                // v77.22.7 — Day-detail modal also opens for days with
                // custody info (assignment or handover), not just for
                // days with events. The per-child custody panel is
                // useful even on "quiet" days, especially when a
                // single-child filter is active.
                const hasCustodyInfo = !!(assignment || handoversForCell.length > 0);
                const onTap = () => {
                  if (isMediator) return;
                  // v77.99.26 — Suppress the day-tap when the user has
                  // just finished a Month-view drag-to-extend over this
                  // cell. Otherwise the pointerup that ends the resize
                  // bubbles and re-opens the day-detail / create dialog.
                  if (monthResizeStateRef.current.justFinished) {
                    monthResizeStateRef.current.justFinished = false;
                    return;
                  }
                  if (dayEvents.length > 0 || hasCustodyInfo) setDayDetail({ date: d, events: dayEvents });
                  else openCreate(d);
                };
                const onLong = () => { if (!isMediator) openCreate(d); };
                return (
                  <div
                    role="button"
                    tabIndex={isMediator ? -1 : 0}
                    key={key}
                    {...longPressProps(onTap, onLong)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onTap(); } }}
                    onDragOver={(e) => { if (dragEvent) { e.preventDefault(); setDragOverKey(key); } }}
                    onDragLeave={() => { if (dragOverKey === key) setDragOverKey(null); }}
                    onDrop={async (e) => {
                      e.preventDefault();
                      setDragOverKey(null);
                      if (dragEvent) await moveEventToDate(dragEvent, d);
                      setDragEvent(null);
                    }}
                    className={`text-left border-r border-b border-[#D9E1E3] p-1.5 overflow-hidden transition-colors select-none relative ${isDragOver || isMonthResizeOver ? "ring-2 ring-inset ring-[#1F9FA4] bg-[#E8F0F1]" : isToday ? "bg-[#EEF3F4]" : isWeekend ? "bg-[#FAFBFB]" : "bg-white"} ${!isDragOver && !isMonthResizeOver && !isToday ? "hover:bg-[#F5F7F8]" : ""} ${isMediator ? "cursor-default" : "cursor-pointer"}`}
                    style={!isDragOver && !isMonthResizeOver && custodyTint ? { backgroundColor: custodyTint } : undefined}
                    data-testid={`day-${key}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className={`text-xs font-mono-data ${isToday ? "text-[#2E3A3F] font-semibold" : "text-[#6F828A]"}`}>{d.getDate()}</div>
                      <div className="flex items-center gap-1">
                        {/* v77.22.22 — Handover indicator. When a
                            single child is selected, we show only that
                            child's handover; in master view we keep
                            showing all children with the ×N counter.
                            Time shown only when ALL handovers share
                            the same time, otherwise we drop the time
                            and let the user open the day-detail for
                            breakdown. */}
                        {handoversForCell.length > 0 || (!scopedChildId && handovers[isoKey]) ? (
                          (() => {
                            const perChild = handoversForCell;
                            const fallback = perChild.length === 0 && handovers[isoKey] ? [{
                              child_name: null,
                              from_parent_id: handovers[isoKey].from_parent_id,
                              to_parent_id: handovers[isoKey].to_parent_id,
                              handover_time: handovers[isoKey].handover_time,
                            }] : perChild;
                            const tooltip = fallback.map((h) => {
                              const lbl = h.child_name ? `${h.child_name}: ` : "";
                              return `${lbl}${memberName(h.from_parent_id)} → ${memberName(h.to_parent_id)}${h.handover_time ? ` at ${h.handover_time}` : ""}`;
                            }).join("\n");
                            const allSameTime = fallback.length > 0 && fallback.every((h) => h.handover_time === fallback[0].handover_time);
                            const sharedTime = allSameTime ? fallback[0].handover_time : null;
                            return (
                              <span
                                title={`Handover today\n${tooltip}`}
                                className="inline-flex items-center gap-0.5 h-3.5 px-0.5 rounded-sm bg-[#E0F2F2] border border-[#A7D8DA] text-[#116B6E]"
                                data-testid={`day-handover-${key}`}
                                aria-label={`Handover today${sharedTime ? ` at ${sharedTime}` : ""}`}
                              >
                                <ArrowLeftRight className="w-2.5 h-2.5" strokeWidth={2} />
                                {sharedTime && (
                                  <span className="text-[8px] font-mono-data leading-none font-semibold" data-testid={`day-handover-time-${key}`}>
                                    {sharedTime}
                                  </span>
                                )}
                                {fallback.length > 1 && (
                                  <span className="text-[8px] font-mono-data leading-none">×{fallback.length}</span>
                                )}
                              </span>
                            );
                          })()
                        ) : null}
                        {assignment?.source === "override" && (
                          <span title={`Custody override${assignment.reason ? `: ${assignment.reason}` : ""}`} className="text-[8px] uppercase tracking-[0.08em] font-semibold px-1 py-0 rounded-sm bg-[#FFF1D6] border border-[#E6CFA2] text-[#8F6B1F]" data-testid={`day-override-${key}`}>override</span>
                        )}
                        {dayEvents.length === 0 && !isMediator && (
                          <Plus className="w-3 h-3 text-transparent group-hover:text-[#6F828A]" strokeWidth={2} />
                        )}
                      </div>
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {/* v77.99.61 — Maintenance overlay is now a
                          single subtle dot-row rather than a
                          €XXX-amount pill. Maintenance has its own
                          page; the calendar's job is to make the
                          handover schedule legible at a glance. The
                          dot row preserves the existence + status
                          signal (paid / late / missed / due) without
                          competing with custody events for visual
                          attention. Tooltip + click target both
                          unchanged. */}
                      {(() => {
                        const chips = maintenanceChips[isoKey] || [];
                        if (chips.length === 0) return null;
                        const cur = chips[0].currency || "EUR";
                        const sym = currencySymbol(cur);
                        const needsSpace = sym.length > 1 && !["CA$", "A$", "NZ$"].includes(sym);
                        const totalSym = needsSpace ? `${sym} ` : sym;
                        const total = chips.reduce((a, c) => a + (c.amount || 0), 0);
                        // Worst-case dot colour: missed > late > paid > due
                        const dotFor = (s) => s === "paid" || s === "overpaid"
                          ? "#1F9FA4"
                          : s === "missed"
                          ? "#C0291D"
                          : s === "late" || s === "part_paid"
                          ? "#E6884A"
                          : "#116B6E";
                        const titles = chips.map((c) => `Maintenance ${c.status} · ${totalSym}${c.amount.toFixed(2)}`).join("\n");
                        return (
                          <a
                            href="/app/maintenance"
                            onClick={(e) => { e.stopPropagation(); }}
                            className="inline-flex items-center gap-1 text-[10px] text-[#6F828A] hover:text-[#116B6E] transition-colors px-1 py-0.5"
                            title={`${chips.length} maintenance payment${chips.length === 1 ? "" : "s"}, total ${totalSym}${total.toFixed(2)}\n${titles}`}
                            data-testid={`day-maintenance-dots-${isoKey}`}
                            aria-label={`${chips.length} maintenance payment${chips.length === 1 ? "" : "s"} on this date`}
                          >
                            {chips.slice(0, 3).map((chip, ci) => (
                              <span
                                key={`md-${chip.arrangement_id}-${ci}`}
                                className="w-1.5 h-1.5 rounded-full"
                                style={{ backgroundColor: dotFor(chip.status) }}
                              />
                            ))}
                            <span className="ml-0.5">maintenance</span>
                          </a>
                        );
                      })()}
                      {dayEvents.slice(0, 2).map((ev) => {
                        const kids = childSwatches(ev.child_ids);
                        // Multi-child colour bar: stack up to 3 colour stops in
                        // a left-edge gradient. Single-child events keep a
                        // solid 3px coloured left border (matches old design).
                        const stops = kids.slice(0, 3).map((k) => k.color || "#1F9FA4");
                        const leftStyle = stops.length === 1
                          ? { borderLeftWidth: 3, borderLeftColor: stops[0] }
                          : stops.length > 1
                            ? { borderLeftWidth: 4, borderLeftStyle: "solid", borderLeftColor: "transparent", backgroundImage: `linear-gradient(to bottom, ${stops.map((c, i) => `${c} ${(i*100)/stops.length}%, ${c} ${((i+1)*100)/stops.length}%`).join(", ")}), linear-gradient(white, white)`, backgroundOrigin: "border-box", backgroundClip: "border-box, padding-box" }
                            : undefined;
                        const draggable = !ev.recurrence && !isMediator && ev.kind !== "contact_visit";
                        // v77.99.26 — Month-view resize-extend is allowed
                        // even on recurring events because the
                        // EditOccurrenceDialog asks the user for scope
                        // (single / future / series) on save. We do
                        // require an explicit `end` so we have a
                        // duration to preserve as we change the date.
                        const monthResizable = !!ev.end && !isMediator;
                        const isThisResizing = monthResize?.eventId === ev.id;
                        // Iter 122.calendar-status — pre-compute the CV
                        // chip style so we render status-coloured chips
                        // with at-a-glance indicators in one place.
                        const cvStyle = ev.kind === "contact_visit" ? cvChipStatusStyle(ev.status) : null;
                        const cvIndicators = ev.kind === "contact_visit" ? [
                          ev.has_gps && { icon: MapPin,      label: "Location captured",    tone: "text-teal-700" },
                          ev.observers_submitted && { icon: FileCheck2, label: "Observer submitted",   tone: "text-emerald-700" },
                          (ev.has_observers && !ev.observers_submitted) && { icon: UserRoundCheck, label: "Observer invited",      tone: "text-slate-600" },
                          ev.attachments_count > 0 && { icon: Paperclip, label: `${ev.attachments_count} attachment${ev.attachments_count === 1 ? "" : "s"}`, tone: "text-slate-600" },
                        ].filter(Boolean) : [];
                        return (
                          <div key={ev.id} className="relative group/chip">
                          <div
                            draggable={draggable}
                            onClick={(e) => { e.stopPropagation(); openEdit(ev); }}
                            onDragStart={(e) => {
                              if (!draggable) { e.preventDefault(); return; }
                              setDragEvent(ev);
                              try { e.dataTransfer.setData("text/plain", ev.id); e.dataTransfer.effectAllowed = "move"; } catch { /* ignore */ }
                            }}
                            onDragEnd={() => { setDragEvent(null); setDragOverKey(null); }}
                            className={`text-[10px] px-1 py-0.5 border rounded-sm truncate flex items-center gap-1 ${
                              cvStyle
                                ? cvStyle.cls
                                : typeClass(ev.type, parentIdx(ev.parent_id))
                            } ${ev.linked_journal_count > 0 ? "border-l-[3px] border-l-[#116B6E]" : ""} ${draggable ? "cursor-grab active:cursor-grabbing" : ""} ${dragEvent?.id === ev.id ? "opacity-40" : ""} ${isThisResizing ? "ring-2 ring-[#1F9FA4] shadow-sm" : ""}`}
                            style={leftStyle}
                            title={`${ev.title}${cvStyle ? ` — ${cvStyle.label}${ev.evidence_ref ? ` · ${ev.evidence_ref}` : ""}` : ""}${ev.linked_journal_count > 0 ? ` — ${ev.linked_journal_count} linked journal ${ev.linked_journal_count === 1 ? "note" : "notes"}` : ""}${cvIndicators.length > 0 ? ` — ${cvIndicators.map((i) => i.label).join(" · ")}` : ""}${draggable ? " — drag to another day to reschedule" : ""}${monthResizable ? " — long-press bottom edge to extend end date" : ""}`}
                            data-testid={`day-event-chip-${ev.id}`}
                            data-cv-status={cvStyle?.statusKey || undefined}
                          >
                            {cvStyle && (
                              <span
                                className="inline-flex items-center justify-center text-[8px] font-semibold tracking-wider px-1 rounded-sm bg-white/70 border border-current flex-shrink-0 leading-none py-px"
                                aria-label={`Contact visit · ${cvStyle.label}${ev.evidence_ref ? ` · ${ev.evidence_ref}` : ""}`}
                                title={`${cvStyle.label}${ev.evidence_ref ? ` · ${ev.evidence_ref}` : ""}`}
                                data-testid={`day-event-cv-badge-${ev.id}`}
                              >CV</span>
                            )}
                            {ev.recurrence && <Repeat className="w-2.5 h-2.5 flex-shrink-0" strokeWidth={1.8} />}
                            {/* v73 — visibility indicator. Private events
                                show a closed-eye glyph so it's obvious
                                the other parent can't see them. Shared
                                events render no icon to keep the chip
                                compact (this is the common case). */}
                            {!ev.is_shared && (
                              <span
                                className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm bg-[#FAF5EE] text-[#825A41] flex-shrink-0 border border-[#E5C8A6]"
                                aria-label="Private — only you can see this"
                                title="Private — only you can see this"
                                data-testid={`day-event-private-${ev.id}`}
                              >
                                <EyeOff className="w-2.5 h-2.5" strokeWidth={2} />
                              </span>
                            )}
                            {ev.linked_journal_count > 0 && (
                              <span
                                className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm bg-[#116B6E] text-white flex-shrink-0"
                                aria-label={`${ev.linked_journal_count} linked journal ${ev.linked_journal_count === 1 ? "note" : "notes"}`}
                              >
                                <BookOpen className="w-2.5 h-2.5" strokeWidth={2.2} />
                              </span>
                            )}
                            {/* Iter 122.calendar-status — at-a-glance CV indicators */}
                            {cvIndicators.map((ind, ix) => {
                              const I = ind.icon;
                              return (
                                <span
                                  key={ix}
                                  className={`inline-flex items-center justify-center w-3.5 h-3.5 flex-shrink-0 ${ind.tone}`}
                                  aria-label={ind.label}
                                  title={ind.label}
                                  data-testid={`day-event-cv-${cvStyle.statusKey}-${ev.id}-ind-${ix}`}
                                >
                                  <I className="w-2.5 h-2.5" strokeWidth={2} />
                                </span>
                              );
                            })}
                            {/* v65 — child colour dot in master view. Hidden when filtered to a single child. */}
                            <ChildDots childIds={ev.child_ids} variant="dotsOnly" className="flex-shrink-0" />
                            <span className="truncate">{ev.title}</span>
                          </div>
                          {/* v77.99.26 — Resize-extend handle. Small bar at
                              the chip's bottom edge; expands to a 12px
                              touch target via the pseudo-:before. Long-
                              press 300ms arms the resize; sub-300ms
                              press falls through to the chip's onClick
                              (opens the edit dialog) so the gesture is
                              additive, not disruptive. */}
                          {monthResizable && (
                            <div
                              role="separator"
                              aria-orientation="horizontal"
                              aria-label="Extend event end date"
                              data-testid={`month-event-resize-handle-${ev.id}`}
                              onPointerDown={(e) => {
                                if (e.button && e.button !== 0) return;
                                e.stopPropagation();
                                const pid = e.pointerId;
                                longPressMonthRef.current = setTimeout(() => {
                                  beginMonthResize(ev, pid);
                                  longPressMonthRef.current = null;
                                }, 300);
                              }}
                              onPointerMove={(e) => {
                                if (monthResizeStateRef.current.current) {
                                  e.preventDefault();
                                  _updateMonthResize(e);
                                } else if (longPressMonthRef.current) {
                                  clearTimeout(longPressMonthRef.current);
                                  longPressMonthRef.current = null;
                                }
                              }}
                              onPointerUp={() => {
                                if (longPressMonthRef.current) {
                                  clearTimeout(longPressMonthRef.current);
                                  longPressMonthRef.current = null;
                                }
                              }}
                              onPointerCancel={_cancelMonthResize}
                              className="absolute left-2 right-2 -bottom-0.5 h-1 rounded-full bg-[#1F9FA4]/0 group-hover/chip:bg-[#1F9FA4]/40 hover:bg-[#1F9FA4] active:bg-[#116B6E] cursor-ns-resize transition-colors touch-none before:absolute before:inset-x-0 before:-top-2 before:h-4 before:content-['']"
                              style={{
                                background: isThisResizing ? "#116B6E" : undefined,
                              }}
                            />
                          )}
                          </div>
                        );
                      })}
                      {dayEvents.length > 2 && (
                        <div className="text-[10px] text-[#1F9FA4] font-medium" data-testid={`day-more-${key}`}>+{dayEvents.length - 2} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="list" className="mt-4 data-[state=inactive]:hidden">
          {visibleEvents.length === 0 ? (
            <div className="border border-[#D9E1E3] bg-white rounded-md p-12 text-center">
              <CalendarDays className="w-8 h-8 mx-auto text-[#6F828A]" strokeWidth={1.5} />
              <p className="mt-3 text-[#6F828A]">
                {(childFilter.size > 0 || parentFilter.size > 0)
                  ? "No events match the selected filters. Clear the filter to see all events."
                  : "No events yet. Add your first pickup, drop-off or custody block."}
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {visibleEvents.map((ev) => {
                const evDate = ev.start ? new Date(ev.start) : null;
                const evIsoKey = evDate
                  ? `${evDate.getFullYear()}-${String(evDate.getMonth() + 1).padStart(2, "0")}-${String(evDate.getDate()).padStart(2, "0")}`
                  : null;
                const ho = evIsoKey ? handovers[evIsoKey] : null;
                return (
                <li
                  key={ev.id}
                  className={`border bg-white rounded-md p-4 ${
                    ev.linked_journal_count > 0
                      ? "border-[#D9E1E3] border-l-4 border-l-[#116B6E] bg-gradient-to-r from-[#F0F8F8] to-white"
                      : "border-[#D9E1E3]"
                  }`}
                  data-testid={`event-${ev.id}`}
                >
                  {/* v77.99.30 — Mobile-safe list layout. The previous
                      `flex flex-wrap justify-between gap-4` outer
                      shrank the content column to nothing at 390px
                      because the action bar's wide buttons forced
                      odd reflow. Switched to a pure `flex-col` stack
                      on mobile (content → divider → actions) and
                      `flex-row` only on `sm+`. No overlap, no
                      mid-word badge wrapping. */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm whitespace-nowrap ${typeClass(ev.type, parentIdx(ev.parent_id))}`}>{TYPE_LABEL[ev.type] || ev.type}</span>
                        {ev.recurrence && <span className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border border-[#D9E1E3] text-[#6F828A] bg-[#EEF3F4] rounded-sm inline-flex items-center gap-1 whitespace-nowrap"><Repeat className="w-3 h-3" strokeWidth={1.8} /> {ev.recurrence.freq}</span>}
                        <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm inline-flex items-center gap-1 whitespace-nowrap ${ev.is_shared ? "border-[#4A6170] bg-[#E6ECEF] text-[#2E3E48]" : "border-[#D9E1E3] bg-[#EEF3F4] text-[#6F828A]"}`}>
                          {ev.is_shared ? <Eye className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                          {ev.is_shared ? "Shared" : "Private"}
                        </span>
                        {ev.status === "proposed" && <span className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border border-[#825A41] text-[#4A3A2E] bg-[#F0E8E3] rounded-sm whitespace-nowrap">Change proposed</span>}
                        {ho && (
                          <span
                            title={`Custody handover: ${memberName(ho.from_parent_id)} → ${memberName(ho.to_parent_id)}${ho.handover_time ? ` at ${ho.handover_time}` : ""}${ho.reason ? ` (${ho.reason})` : ""}`}
                            className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border border-[#A7D8DA] bg-[#E0F2F2] text-[#116B6E] rounded-sm inline-flex items-center gap-1 whitespace-nowrap"
                            data-testid={`event-handover-${ev.id}`}
                          >
                            <ArrowLeftRight className="w-3 h-3" strokeWidth={1.8} />
                            Handover{ho.handover_time ? ` ${ho.handover_time}` : ""}
                          </span>
                        )}
                        {ev.linked_journal_count > 0 && (
                          <button
                            type="button"
                            onClick={() => openLinkedJournal(ev)}
                            title={`${ev.linked_journal_count} linked journal ${ev.linked_journal_count === 1 ? "entry" : "entries"}`}
                            className="text-[11px] uppercase tracking-[0.08em] font-semibold px-2.5 py-1 bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-sm inline-flex items-center gap-1.5 transition-colors shadow-sm whitespace-nowrap"
                            data-testid={`event-linked-journal-${ev.id}`}
                          >
                            <BookOpen className="w-3 h-3" strokeWidth={2.2} />
                            {ev.linked_journal_count} {ev.linked_journal_count === 1 ? "Journal note" : "Journal notes"}
                          </button>
                        )}
                      </div>
                      <div className="mt-2 font-medium text-[#2E3A3F] inline-flex items-center gap-2 break-words">
                        {ev.linked_journal_count > 0 && (
                          <span
                            className="inline-flex items-center justify-center w-5 h-5 rounded-sm bg-[#116B6E] text-white flex-shrink-0"
                            aria-hidden="true"
                          >
                            <BookOpen className="w-3 h-3" strokeWidth={2.2} />
                          </span>
                        )}
                        <span className="break-words">{ev.title}</span>
                      </div>
                      <div className="text-sm text-[#6F828A] font-mono-data mt-1 break-words">{formatDT(ev.start)} → {formatDT(ev.end)}</div>
                      {childSwatches(ev.child_ids).length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {childSwatches(ev.child_ids).map((c) => (
                            <span key={c.id} className="inline-flex items-center gap-1 text-[11px] text-[#6F828A] whitespace-nowrap">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color || "#1F9FA4" }}></span>{c.name}
                            </span>
                          ))}
                        </div>
                      )}
                      {ev.notes && <div className="text-sm text-[#6F828A] mt-2 break-words">{ev.notes}</div>}
                      {ev.status === "proposed" && ev.proposed_changes && (
                        <div className="mt-3 p-3 bg-[#F0E8E3] border border-[#D9E1E3] rounded-sm text-sm">
                          <div className="text-xs uppercase tracking-[0.1em] text-[#6F828A] mb-1">Proposed changes</div>
                          {ev.proposed_changes.start && <div className="font-mono-data break-words">Start → {formatDT(ev.proposed_changes.start)}</div>}
                          {ev.proposed_changes.end && <div className="font-mono-data break-words">End → {formatDT(ev.proposed_changes.end)}</div>}
                        </div>
                      )}
                    </div>
                    <div
                      className="flex flex-wrap gap-2 sm:w-auto pt-3 sm:pt-0 border-t border-[#EEF3F4] sm:border-t-0 -mx-4 sm:mx-0 px-4 sm:px-0"
                      data-testid={`event-actions-${ev.id}`}
                    >
                      {!isMediator && ev.status === "proposed" && ev.proposed_by !== user.id && (
                        <>
                          <Button size="sm" onClick={() => approve(ev.id)} className="bg-[#1F9FA4] hover:bg-[#116B6E] rounded-sm h-9 sm:h-8 flex-1 sm:flex-initial min-w-[120px] sm:min-w-0" data-testid={`approve-${ev.id}`}>Agree</Button>
                          <Button size="sm" variant="outline" onClick={() => reject(ev.id)} className="border-[#D9E1E3] rounded-sm h-9 sm:h-8 flex-1 sm:flex-initial min-w-[120px] sm:min-w-0" data-testid={`reject-${ev.id}`}>Not agree</Button>
                        </>
                      )}
                      {!isMediator && ev.status === "confirmed" && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => openEdit(ev)} className="border-[#D9E1E3] rounded-sm h-9 sm:h-8 flex-1 sm:flex-initial min-w-[90px] sm:min-w-0" data-testid={`edit-${ev.id}`}>
                            <Pencil className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setProposeFor(ev); setProposeChanges({ start: toLocalInput(ev.start), end: toLocalInput(ev.end), note: "" }); }} className="border-[#D9E1E3] rounded-sm h-9 sm:h-8 flex-1 sm:flex-initial min-w-[140px] sm:min-w-0" data-testid={`propose-${ev.id}`}>Request change</Button>
                          <Button size="sm" variant="outline" onClick={() => openJournalNote(ev)} className="border-[#D9E1E3] rounded-sm h-9 sm:h-8 flex-1 sm:flex-initial min-w-[140px] sm:min-w-0" data-testid={`journal-note-${ev.id}`}>
                            <BookOpen className="w-3 h-3 mr-1" /> Journal note
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => exportEventToCalendar(ev)} className="border-[#D9E1E3] rounded-sm h-9 sm:h-8 flex-1 sm:flex-initial min-w-[140px] sm:min-w-0" data-testid={`add-to-calendar-${ev.id}`} title="Add to Apple, Google or Outlook calendar">
                            <CalendarPlus className="w-3 h-3 mr-1" /> Add to calendar
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
                );
              })}
            </ul>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!proposeFor} onOpenChange={(v) => !v && setProposeFor(null)}>
        <DialogContent preventOutsideClose className="bg-white border-[#D9E1E3]">
          <DialogHeader><DialogTitle className="font-heading">Request a change</DialogTitle></DialogHeader>
          <p className="text-sm text-[#6F828A]">Your co-parent will review this and either agree or not agree. The event stays unchanged until they agree. Requests can be amended.</p>
          <div className="space-y-3">
            <div><Label>New start</Label><Input type="datetime-local" value={proposeChanges.start} onChange={(e) => setProposeChanges({ ...proposeChanges, start: e.target.value })} data-testid="propose-start-input" /></div>
            <div><Label>New end</Label><Input type="datetime-local" value={proposeChanges.end} onChange={(e) => setProposeChanges({ ...proposeChanges, end: e.target.value })} data-testid="propose-end-input" /></div>
            <div><Label>Note to co-parent</Label><Textarea value={proposeChanges.note} onChange={(e) => setProposeChanges({ ...proposeChanges, note: e.target.value })} placeholder="Briefly explain the reason…" data-testid="propose-note-input" /></div>
          </div>
          <DialogFooter><Button onClick={submitProposal} className="bg-[#1F9FA4] hover:bg-[#178085]" data-testid="propose-submit-button">Send request</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <DayDetailDialog
        dayDetail={dayDetail}
        setDayDetail={setDayDetail}
        selectedChildId={selectedChildId}
        childFilter={childFilter}
        setChildFilter={setChildFilter}
        setParentFilter={setParentFilter}
        childList={children}
        members={members}
        custody={custody}
        childCustodyForDate={childCustodyForDate}
        handoversByChild={handoversByChild}
        memberName={memberName}
        parentIdx={parentIdx}
        typeClass={typeClass}
        childSwatches={childSwatches}
        custodyBgColor={custodyBgColor}
        approve={approve}
        reject={reject}
        openEdit={openEdit}
        setProposeFor={setProposeFor}
        setProposeChanges={setProposeChanges}
        openJournalNote={openJournalNote}
        exportEventToCalendar={exportEventToCalendar}
        openLinkedJournal={openLinkedJournal}
        loadCustody={loadCustody}
        isMediator={isMediator}
        user={user}
      />

      {/* Edit dialog with scope selection */}
      <EditOccurrenceDialog
        editOccurrence={editOccurrence}
        setEditOccurrence={setEditOccurrence}
        childList={children}
        eligibleParentsFor={eligibleParentsFor}
        toggleChildInForm={toggleChildInForm}
        submitEdit={submitEdit}
        cancelOccurrence={cancelOccurrence}
        openLinkedJournal={openLinkedJournal}
        openJournalNote={openJournalNote}
        exportEventToCalendar={exportEventToCalendar}
        isMediator={isMediator}
      />

      <ParentingScheduleDialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        members={members}
        currentUserId={user?.id}
        isPrimaryParent={!!user?.is_primary_parent}
        existing={custody.schedule}
        childList={children}
        byChild={custody.byChild}
        onSaved={() => { setScheduleDialogOpen(false); loadCustody(); loadProposals(); }}
      />

      {/* v77.99.78 — One-click .ics export. Range + child + handovers
          toggle. Hidden for mediators (they download a read-only PDF
          via the Court Bundle page instead). */}
      <CalendarExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        childList={children}
        monthOffset={monthOffset}
      />
    </div>
  );
}
